/**
 * The dashboard's single view of the API surface.
 *
 * Nothing here redefines a server shape. Wire payloads come from the shared
 * contract module, and the admin-only row types (users, audit, apps, release
 * detail, compatibility, analytics overview) are re-exported straight from the
 * server modules that produce them, so a field renamed in a route breaks this
 * build instead of silently rendering `undefined`. Every import is
 * `import type`:
 * esbuild erases them, so zod, express, drizzle and better-sqlite3 never reach
 * the browser bundle.
 *
 * What is declared locally are the response envelopes the REST layer wraps
 * those rows in (`{ users: [...] }`, `{ release: ... }`, …) — they exist only
 * as object literals inside `res.json(...)` — plus the request/query shapes the
 * forms submit and the option lists they render. Each option list is
 * `satisfies`-checked against its contract union, so a new platform, status or
 * role cannot silently go missing from the UI.
 */
export type {
  ActiveUsersPoint,
  ActiveUsersResponse,
  ActorKind,
  ApiError,
  ChangePasswordRequest,
  CounterType,
  CreateUserRequest,
  DeviceEventType,
  FeatureActorRow,
  FeatureDayPoint,
  FeatureDetailResponse,
  FeatureUsageRow,
  LoginRequest,
  LoginResponse,
  Platform,
  PublishReleaseRequest,
  ReleaseStatus,
  ReleaseSummary,
  SessionUser,
  UpdateUserRequest,
  UserRole,
  VersionAdoptionRow,
} from "../../src/contract.js";
export type { AppSummary, ChannelSummary } from "../../src/domain/registry.js";
export type { OverviewResponse } from "../../src/domain/analytics.js";
export type { AuditEntry } from "../../src/routes/audit.js";
export type {
  BuildKind,
  BuildSummary,
  PromoteBuildResponse,
} from "../../src/routes/builds.js";
export type {
  CompatibilityReport,
  KnownTargetsResponse,
  ReleaseDetailResponse,
  ReleaseEventCounts,
} from "../../src/routes/releases.js";
export type { KnownTarget } from "../../src/domain/target.js";
export type {
  SetupApp,
  SetupChannel,
  SetupPlatformState,
  SetupResponse,
} from "../../src/routes/setup.js";
export type { UserSummary } from "../../src/routes/users.js";

// Locally referenced by the envelopes below; `export type ... from` above only
// re-exports, it does not create bindings in this module's scope.
import type {
  ActorKind,
  CounterType,
  DeviceEventType,
  FeatureUsageRow,
  Platform,
  PublishReleaseRequest,
  ReleaseStatus,
  ReleaseSummary,
  UpdateUserRequest,
  UserRole,
  VersionAdoptionRow,
} from "../../src/contract.js";
import type { AppSummary } from "../../src/domain/registry.js";
import type { AuditEntry } from "../../src/routes/audit.js";
import type { BuildSummary } from "../../src/routes/builds.js";
import type { CompatibilityReport } from "../../src/routes/releases.js";
import type { UserSummary } from "../../src/routes/users.js";

/* -------------------------------------------------------------------------- */
/* Auth                                                                       */
/* -------------------------------------------------------------------------- */

/** Body of POST /v1/auth/refresh and POST /v1/auth/logout. */
export interface RefreshRequest {
  refreshToken: string;
}

export interface LogoutResponse {
  ok: true;
}

/* -------------------------------------------------------------------------- */
/* Users                                                                      */
/* -------------------------------------------------------------------------- */

export interface UserListResponse {
  users: UserSummary[];
}

export interface UserResponse {
  user: UserSummary;
}

/** PATCH /v1/users/:id — the contract patch body plus the path parameter. */
export interface UpdateUserInput extends UpdateUserRequest {
  id: string;
}

/* -------------------------------------------------------------------------- */
/* Audit                                                                      */
/* -------------------------------------------------------------------------- */

export interface AuditQuery {
  action?: string;
  actor?: string;
  limit: number;
  offset: number;
}

export interface AuditListResponse {
  entries: AuditEntry[];
  /** Matching rows ignoring limit/offset, so the pager can bound itself. */
  total: number;
  limit: number;
  offset: number;
}

/* -------------------------------------------------------------------------- */
/* Apps                                                                       */
/* -------------------------------------------------------------------------- */

export interface AppListResponse {
  apps: AppSummary[];
}

export interface AppResponse {
  app: AppSummary;
}

export interface CreateAppRequest {
  slug: string;
  name: string;
  storeUrlAndroid?: string;
  storeUrlIos?: string;
}

export interface CreateChannelRequest {
  /** App slug taken from the path, not the body. */
  slug: string;
  name: string;
}

/* -------------------------------------------------------------------------- */
/* Releases                                                                   */
/* -------------------------------------------------------------------------- */

export interface ReleaseListQuery {
  app?: string;
  channel?: string;
  platform?: Platform;
  status?: ReleaseStatus;
  limit: number;
  offset: number;
}

export interface ReleaseListResponse {
  releases: ReleaseSummary[];
  total: number;
}

export interface ReleaseResponse {
  release: ReleaseSummary;
}

/**
 * POST /v1/releases. The bundle's own identity — app, platform, fingerprint,
 * native version, commit — is read out of `output/ota-meta.json` inside the
 * zip, so `meta` only carries the operator's decisions.
 */
export interface PublishReleaseInput {
  bundle: File;
  meta: PublishReleaseRequest;
}

/**
 * The upload answers with the compatibility of the bundle it just read: the
 * operator reviews that before deciding whether to activate, which is why the
 * upload itself never activates.
 */
export interface PublishReleaseResponse {
  release: ReleaseSummary;
  compatibility: CompatibilityReport;
}

export interface HaltReleaseInput {
  id: string;
  reason?: string;
}

/**
 * `toSeq` omitted asks the server to pick the newest compatible (same
 * fingerprint) release below `id`; supplying it republishes that exact seq.
 */
export interface RollbackReleaseInput {
  id: string;
  toSeq?: number;
  reason?: string;
}

export interface SetRolloutInput {
  id: string;
  rolloutPercent: number;
}

/**
 * POST /v1/releases/:id/activate. `confirmNoDevices` is the operator saying
 * "yes, I know no device runs this fingerprint yet" — the usual case right
 * after a new native build, and a mistake every other time.
 */
export interface ActivateReleaseInput {
  id: string;
  confirmNoDevices?: boolean;
}

export interface ReleaseTargetsQuery {
  app: string;
  channel: string;
  platform: Platform;
}

/**
 * POST /v1/releases/:id/copy. Republishes bytes that are already on the server
 * against another native target, which is how a bundle proven on staging
 * reaches production without a second upload.
 */
export interface CopyReleaseInput {
  id: string;
  channel?: string;
  fingerprint: string;
  nativeVersion?: string;
  activate: boolean;
  confirmNoDevices?: boolean;
}

/**
 * DELETE /v1/releases/:id. Only the row (and, when nothing else points at the
 * same object, the zip) goes away: `releaseSeq` is reported back because that
 * number stays burned — the next upload keeps counting up from it.
 */
export interface DeleteReleaseResponse {
  deleted: string;
  releaseSeq: number;
}

/* -------------------------------------------------------------------------- */
/* Builds                                                                     */
/* -------------------------------------------------------------------------- */

export interface BuildListQuery {
  app?: string;
  channel?: string;
  platform?: Platform;
  limit: number;
  offset: number;
}

export interface BuildListResponse {
  builds: BuildSummary[];
  total: number;
}

export interface BuildResponse {
  build: BuildSummary;
}

/**
 * JSON part of the multipart upload. Platform and kind are absent on purpose:
 * the server derives both from the file extension, so an `.ipa` can never be
 * filed under android by a mistyped form.
 */
export interface UploadBuildRequest {
  app: string;
  channel: string;
  version: string;
  buildNumber?: string;
  fingerprint?: string;
  notes?: string;
}

export interface UploadBuildInput {
  binary: File;
  meta: UploadBuildRequest;
}

/**
 * POST /v1/builds/:id/share. The URL carries its own download token, so it
 * opens on a tester's phone without a dashboard session — and stops working
 * at `expiresAt`, which is why the UI always shows that moment next to it.
 */
export interface ShareBuildResponse {
  url: string;
  expiresAt: string | null;
}

/**
 * POST /v1/builds/:id/promote. Declares this binary the one its channel ships,
 * which is what makes every older install start being told to update.
 */
export interface PromoteBuildInput {
  id: string;
  note?: string;
  blocking: boolean;
}

export interface DeleteBuildResponse {
  deleted: string;
}

/* -------------------------------------------------------------------------- */
/* Analytics                                                                  */
/* -------------------------------------------------------------------------- */

/** Query accepted by /metrics/active-users, /features and /adoption. */
export interface AnalyticsParams {
  app: string;
  channel?: string;
  platform?: Platform;
  from: string;
  to: string;
  actor: ActorKind;
}

export interface FeatureUsageQuery extends AnalyticsParams {
  limit?: number;
}

/**
 * /metrics/feature-detail — the same scope as the table plus the row that was
 * clicked. `q` searches the employee list by NIP or name and `limit` pages it;
 * neither touches the daily series.
 */
export interface FeatureDetailParams extends AnalyticsParams {
  type: CounterType;
  key: string;
  q?: string;
  limit?: number;
}

export interface FeatureUsageResponse {
  rows: FeatureUsageRow[];
}

export interface VersionAdoptionResponse {
  rows: VersionAdoptionRow[];
}

/**
 * /metrics/overview uses fixed windows, so it only takes app + channel — plus
 * the actor basis, which must match the charts' or the cards would label the
 * same page with two different definitions of "active".
 */
export interface OverviewParams {
  app: string;
  channel?: string;
  actor: ActorKind;
}

/* -------------------------------------------------------------------------- */
/* Option lists for the forms                                                 */
/* -------------------------------------------------------------------------- */

export const PLATFORM_OPTIONS = ["android", "ios"] as const satisfies readonly Platform[];

export const RELEASE_STATUS_OPTIONS = [
  "draft",
  "active",
  "halted",
  "superseded",
  "rolled_back",
] as const satisfies readonly ReleaseStatus[];

export const USER_ROLE_OPTIONS = [
  "admin",
  "releaser",
  "analyst",
  "viewer",
] as const satisfies readonly UserRole[];

export const ACTOR_KIND_OPTIONS = ["device", "user"] as const satisfies readonly ActorKind[];

export const DEVICE_EVENT_TYPE_OPTIONS = [
  "downloaded",
  "installed",
  "failed",
  "rolled_back",
] as const satisfies readonly DeviceEventType[];
